import { useEffect, useMemo, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { Reveal, Overline } from "../components/motion";
import { PROGRAMS, CALENDAR, COURSE_CATALOGUE } from "../data/content";
import { Clock, Users, BadgeCheck, CircleDollarSign } from "lucide-react";

const ProgramCard = ({ p, flip }) => (
  <article id={p.id} data-testid={`program-detail-${p.id}`} className="scroll-mt-28 grid lg:grid-cols-12 gap-10 items-start py-16 border-b border-border last:border-0">
    <div className={`lg:col-span-5 ${flip ? "lg:order-2" : ""}`}>
      <Reveal>
        <div className="relative">
          <div className={`absolute -top-3 ${flip ? "-right-3" : "-left-3"} w-full h-full border border-gold/50 rounded-sm pointer-events-none`} />
          <div className="overflow-hidden rounded-sm aspect-[4/3]">
            <img src={p.image} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 ease-out hover:scale-[1.04]" />
          </div>
        </div>
      </Reveal>
    </div>
    <div className={`lg:col-span-7 ${flip ? "lg:order-1" : ""}`}>
      <Reveal>
        <div className="flex items-baseline gap-4">
          <span className="font-serif text-accent/50 text-xl">{p.index}</span>
          <Overline>{p.category}</Overline>
        </div>
        <h2 className="mt-3 font-serif text-3xl md:text-4xl tracking-tight text-navy">{p.title}</h2>
        <p className="mt-2 font-serif italic text-lg text-accent">{p.credential}</p>
        <p className="mt-5 text-muted-foreground leading-relaxed">{p.summary}</p>

        <div className="mt-7 grid sm:grid-cols-2 gap-4 text-sm">
          {[
            [Clock, "Duration", p.duration],
            [Users, "Class Size", p.classSize],
            [BadgeCheck, "Who It's For", p.whoFor],
            [CircleDollarSign, "Tuition", p.tuition],
          ].map(([Icon, label, value]) => (
            <div key={label} className="flex gap-3 items-start bg-secondary/40 border border-border rounded-sm p-4">
              <Icon size={17} className="text-accent mt-0.5 shrink-0" />
              <div>
                <p className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground">{label}</p>
                <p className="mt-1 text-navy leading-snug">{value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-7 grid md:grid-cols-2 gap-8">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-accent mb-3">What You'll Learn</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {p.learn.map((l) => (
                <li key={l} className="flex gap-2">
                  <span className="text-gold mt-0.5">✦</span>
                  {l}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-accent mb-3">Outcomes</p>
            <ul className="space-y-2 text-sm text-muted-foreground">
              {p.outcomes.map((o) => (
                <li key={o} className="flex gap-2">
                  <span className="text-gold mt-0.5">✦</span>
                  {o}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Reveal>
    </div>
  </article>
);

export default function ProgramsPage() {
  const { hash } = useLocation();
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("All");
  const [cert, setCert] = useState("All");
  const [len, setLen] = useState("All");

  const CERTS = useMemo(
    () => [...new Set(COURSE_CATALOGUE.flatMap((g) => g.courses.map((c) => c.certification)))],
    []
  );
  const LENGTHS = ["Short Course", "40–80 Hours", "Multi-Week", "Placement"];

  const filteredCatalogue = COURSE_CATALOGUE.map((g) => ({
    ...g,
    courses: g.courses.filter(
      (c) =>
        (cat === "All" || g.category === cat) &&
        (cert === "All" || c.certification === cert) &&
        (len === "All" || c.length === len) &&
        (query.trim() === "" || c.name.toLowerCase().includes(query.trim().toLowerCase()))
    ),
  })).filter((g) => g.courses.length > 0);
  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [hash]);

  return (
    <main className="pt-20">
      <section className="py-20 md:py-28 bg-navy text-primary-foreground">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Overline light>Programs of Study</Overline>
          <h1 className="mt-4 font-serif text-5xl md:text-6xl tracking-tight leading-[1.05]">
            Focused training.
            <br />
            <span className="italic text-gold">Recognised credentials.</span>
          </h1>
          <p className="mt-6 max-w-2xl text-primary-foreground/60 leading-relaxed">
            Every program is delivered in small, focused classes with a strong emphasis on
            hands-on learning, practical problem-solving, and industry standards — formally
            accredited and respected within the industry.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        {PROGRAMS.map((p, i) => (
          <ProgramCard key={p.id} p={p} flip={i % 2 === 1} />
        ))}
      </section>

      <section data-testid="course-catalogue" className="py-24 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Overline>Full Course Catalogue</Overline>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-navy leading-tight max-w-2xl">
              Every course we teach, in one place.
            </h2>
            <p className="mt-5 max-w-2xl text-muted-foreground leading-relaxed">
              From the complete Cytech bicycle mechanic pathway to guide training, wilderness
              medicine, and media courses — each can be taken individually or as part of a program.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12 space-y-5">
            <div className="max-w-md">
              <input
                data-testid="catalogue-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search courses…"
                className="w-full bg-transparent border border-border rounded-sm px-4 py-3 text-sm text-navy placeholder:text-muted-foreground/60 focus:outline-none focus:border-accent transition-colors duration-300"
              />
            </div>
            <div data-testid="filter-category" className="flex flex-wrap gap-2">
              {["All", ...COURSE_CATALOGUE.map((g) => g.category)].map((c) => (
                <button
                  key={c}
                  data-testid={`filter-cat-${c === "All" ? "all" : c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  onClick={() => setCat(c)}
                  className={`px-4 py-1.5 rounded-full text-xs tracking-wide border transition-colors duration-300 ${
                    cat === c ? "bg-navy text-primary-foreground border-navy" : "border-border text-muted-foreground hover:border-accent hover:text-navy"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-x-8 gap-y-3">
              <div data-testid="filter-certification" className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mr-1">Certification</span>
                {["All", ...CERTS].map((c) => (
                  <button
                    key={c}
                    data-testid={`filter-cert-${c === "All" ? "all" : c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setCert(c)}
                    className={`px-3 py-1 rounded-full text-[11px] border transition-colors duration-300 ${
                      cert === c ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent hover:text-navy"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
              <div data-testid="filter-duration" className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] tracking-[0.18em] uppercase text-muted-foreground mr-1">Duration</span>
                {["All", ...LENGTHS].map((l) => (
                  <button
                    key={l}
                    data-testid={`filter-len-${l === "All" ? "all" : l.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => setLen(l)}
                    className={`px-3 py-1 rounded-full text-[11px] border transition-colors duration-300 ${
                      len === l ? "bg-accent text-accent-foreground border-accent" : "border-border text-muted-foreground hover:border-accent hover:text-navy"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          {filteredCatalogue.length === 0 ? (
            <p data-testid="catalogue-empty" className="mt-16 text-center text-muted-foreground text-sm">
              No courses match these filters. <button className="text-accent underline underline-offset-4" onClick={() => { setQuery(""); setCat("All"); setCert("All"); setLen("All"); }}>Clear filters</button>
            </p>
          ) : (
          <div className="mt-16 space-y-0 divide-y divide-border border-y border-border">
            {filteredCatalogue.map((group, gi) => (
              <Reveal key={group.category} delay={0.05}>
                <div className="py-10 grid lg:grid-cols-12 gap-8" data-testid={`catalogue-group-${gi}`}>
                  <div className="lg:col-span-4 flex gap-6 items-start">
                    <span className="font-serif text-5xl leading-none text-accent/40 select-none">
                      {String(gi + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <h3 className="font-serif text-2xl text-navy leading-snug">{group.category}</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{group.blurb}</p>
                    </div>
                  </div>
                  <ul className="lg:col-span-8 grid sm:grid-cols-2 gap-x-10 gap-y-4">
                    {group.courses.map((c) => (
                      <li key={c.slug}>
                        <Link
                          to={`/courses/${c.slug}`}
                          data-testid={`course-link-${c.slug}`}
                          className="flex gap-3 items-baseline text-sm group"
                        >
                          <span className="text-gold shrink-0">✦</span>
                          <div>
                            <span className="text-navy font-medium transition-colors duration-300 group-hover:text-accent border-b border-transparent group-hover:border-accent/40">
                              {c.name}
                            </span>
                            <span className="block text-xs text-muted-foreground mt-0.5">{c.note}</span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
          )}
        </div>
      </section>

      <section data-testid="course-calendar" className="py-24 bg-secondary/30 border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Overline>2026 Course Calendar</Overline>
            <h2 className="mt-4 font-serif text-4xl tracking-tight text-navy">Upcoming intakes</h2>
            <p className="mt-4 max-w-2xl text-sm text-muted-foreground leading-relaxed">
              Course dates are subject to change up to 60 days prior to the start date.
              Please contact admissions to confirm availability.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-10 overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b-2 border-navy/20 text-left">
                  {["Course", "Month", "Dates", "Times"].map((h) => (
                    <th key={h} className="py-4 pr-6 text-xs tracking-[0.2em] uppercase text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CALENDAR.map((c, i) => (
                  <tr key={i} data-testid={`calendar-row-${i}`} className="border-b border-border hover:bg-secondary/50 transition-colors duration-300">
                    <td className="py-4 pr-6 font-medium text-navy">{c.course}</td>
                    <td className="py-4 pr-6 text-muted-foreground">{c.month}</td>
                    <td className="py-4 pr-6 text-muted-foreground">{c.dates}</td>
                    <td className="py-4 text-muted-foreground">{c.times}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
